import { msg } from '@lingui/macro'

import * as React from 'react'

import {
  TFPaymentMethod,
  TMReSubmitOrderVariables,
  useMReSubmitOrder,
} from '~graphql/generated/clientGraphql'
import { useTrans } from '~logic/i18n/useTrans'
import { useLockedOrderCtx } from '~shared/segments/OrderLockedScreen'
import { isPostPaidMethod } from '~shared/segments/PaymentMethods'

import { getPaymentCheckScreenRoute } from '../../../helpers/getPaymentCheckScreenRoute'
import { useOrderSubmitHandlers } from '../../FormScreen/hooks/useOrderSubmitHandlers'

type TSubmitOrderInput = {
  paymentMethod: Nullable<TFPaymentMethod>
}

const NO_TIP = 0
const ERROR_MESSAGE = msg`We are sorry, the payment method could not be changed, you can continue by creating a new order.`

/**
 * @Returns `submitOrder` callback to:
 * - submit the order with the changed payment method (and possibly also the tip value) to the BE,
 * - handle the payment.
 */
export const useOrderReSubmit = () => {
  const { trans } = useTrans()
  const [mReSubmitOrder] = useMReSubmitOrder()
  const submitHandlers = useOrderSubmitHandlers()

  const order = useLockedOrderCtx()

  // transform obtainer values into variables, submit them to BE via M,
  // retrieve payment gate URL (for pre-pay methods) & navigate to the next step
  const submitOrder = React.useCallback(
    async (formValues: TSubmitOrderInput) => {
      const { navigateToGateway, navigateToSubmittedScreen, getUrlsFromResult, onSubmitFailed } =
        submitHandlers

      // without paymentMethod & order we shouldn’t ever get here
      // the customer contact (email, phone) should be also already there from the first order submit
      if (formValues.paymentMethod === null || !order) {
        onSubmitFailed([trans(ERROR_MESSAGE)])
        return
      }

      const isPostPaidSelected = isPostPaidMethod(formValues.paymentMethod.paymentGate.enum)

      const paymentCheckScreenRoute = getPaymentCheckScreenRoute()

      const variables: TMReSubmitOrderVariables = {
        orderId: order.id,
        paymentMethodId: formValues.paymentMethod.id,
        tip: isPostPaidSelected ? NO_TIP : null,
        // return screen URLs
        returnUrl: paymentCheckScreenRoute,
      }

      // send order info (payment method & tip) to the BE to be validated and handle errors
      const { data, errors } = await mReSubmitOrder({ variables, fetchPolicy: `network-only` })

      if (errors) return

      // get gatewayUrls to proceed with the payment
      const gatewayUrls = getUrlsFromResult(data?.submitResult ?? null)

      if (!gatewayUrls) {
        return
      }

      // * post-paid methods
      if (isPostPaidSelected) {
        navigateToSubmittedScreen(order.id)
        return
      }

      // * pre-paid methods
      navigateToGateway({ gatewayUrls, orderId: order.id })
    },
    [mReSubmitOrder, order, submitHandlers, trans],
  )

  return { submitOrder }
}
